import { supabase } from "@/integrations/supabase/client";
import { FRESH_SIGN_IN_MS, nativeBrowserOAuth, readBrowserHandoff, type BrowserHandoff } from "@/lib/nativeAuthHandoff";

/**
 * Helpers for the in-app browser (SFSafariViewController / Custom Tab) that
 * runs Google or Apple sign-in for the native apps. That browser is a plain
 * website visit: no Capacitor, and no shared storage with the app.
 */

const AUTH_KEY = /^sb-.*-auth-token/;

/** Drop this browser's copy of the session. Local only: signing out would revoke the session the app is about to use. */
export const forgetBrowserSession = () => {
  supabase.auth.stopAutoRefresh().catch(() => undefined);
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && AUTH_KEY.test(key)) window.localStorage.removeItem(key);
    }
  } catch {
    // storage unavailable
  }
};

/** Leave this page for another (kept separate so tests can watch it). */
export const replaceBrowserLocation = (url: string) => window.location.replace(url);

/** True while this browser is part way through a sign-in for one of the apps. */
export const isNativeHandoffBrowser = () => {
  try {
    return nativeBrowserOAuth.enabled && readBrowserHandoff() !== null;
  } catch {
    return false;
  }
};

/**
 * A session this browser may hand to the app: it must be a sign-in that
 * finished after the attempt started here (a different sign-in time from any
 * session that was already in this browser) and within the freshness window.
 */
export const isNewSignInFor = (
  record: Pick<BrowserHandoff, "prior" | "createdAt"> | null,
  session: { refresh_token?: string; user?: { last_sign_in_at?: string | null } } | null,
  now = Date.now(),
) => {
  const at = session?.user?.last_sign_in_at ?? null;
  if (!record || !session?.refresh_token || !at) return false;
  const signedInAt = Date.parse(at);
  if (!Number.isFinite(signedInAt) || signedInAt < record.createdAt || signedInAt > now || now - signedInAt > FRESH_SIGN_IN_MS) return false;
  if (record && record.prior && record.prior === at) return false;
  return true;
};

/** Signs that the provider has finished and returned to some page on our site. */
export const hasProviderReturn = (search: string, hash: string) =>
  /(?:^|[?&#])(error|error_description|access_token|code)=/.test(`${search}${hash}`);
